import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShareNetwork, DownloadSimple, Copy, WhatsappLogo, FacebookLogo, XLogo, TelegramLogo, RedditLogo, ChatCircleText, EnvelopeSimple, LinkedinLogo } from '@phosphor-icons/react';
import { renderShareCard, shareText, shareUrl } from '@/lib/shareCard';

const platforms = (text, url) => {
  const t = encodeURIComponent(text), u = encodeURIComponent(url);
  return [
    { id: 'whatsapp', label: 'WhatsApp', icon: WhatsappLogo, href: `https://wa.me/?text=${t}` },
    { id: 'facebook', label: 'Facebook', icon: FacebookLogo, href: `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}` },
    { id: 'x', label: 'X', icon: XLogo, href: `https://twitter.com/intent/tweet?text=${t}` },
    { id: 'telegram', label: 'Telegram', icon: TelegramLogo, href: `https://t.me/share/url?url=${u}&text=${t}` },
    { id: 'reddit', label: 'Reddit', icon: RedditLogo, href: `https://www.reddit.com/submit?url=${u}&title=${encodeURIComponent(text.split('\n')[0])}` },
    { id: 'linkedin', label: 'LinkedIn', icon: LinkedinLogo, href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
    { id: 'sms', label: 'Text / SMS', icon: ChatCircleText, href: `sms:?&body=${t}` },
    { id: 'email', label: 'Email', icon: EnvelopeSimple, href: `mailto:?subject=${encodeURIComponent('Red flag read — unmanipulated')}&body=${t}` },
  ];
};

export default function ShareCard({ data }) {
  const [open, setOpen] = useState(false);
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setOpen(false); setCard(null); }, [data]);

  const ensure = async () => {
    if (card) return card;
    setBusy(true);
    try { const c = await renderShareCard(data); setCard(c); return c; }
    finally { setBusy(false); }
  };

  const text = shareText(data);
  const url = shareUrl();

  const nativeShare = async () => {
    const c = await ensure();
    const f = new File([c.blob], 'red-flag.png', { type: 'image/png' });
    try {
      if (navigator.canShare?.({ files: [f] })) await navigator.share({ files: [f], text, title: 'Red flag read' });
      else if (navigator.share) await navigator.share({ text, url, title: 'Red flag read' });
      else { await download(); await copy(); }
    } catch (e) { if (e?.name !== 'AbortError') toast.error('Could not open share sheet'); }
  };

  const download = async () => {
    const c = await ensure();
    const a = document.createElement('a'); a.href = c.dataUrl; a.download = 'unmanipulated-red-flag.png'; a.click();
    toast.success('Card saved · attach it to your message');
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success('Warning text + link copied'); } catch { toast.error('Copy failed'); }
  };

  const openPlatform = async (p) => {
    await download();
    window.open(p.href, '_blank', 'noopener');
  };

  return (
    <div className="mt-8 border-t border-zinc-800 pt-6" data-testid="share-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="label-mini">Warn a friend</h4>
          <p className="mt-1 text-xs text-zinc-500">One tap · a red‑flag card with the score and tactics. No names, no quotes, nothing stored.</p>
        </div>
        <button data-testid="share-card-toggle" onClick={async () => { setOpen(o => !o); if (!open) await ensure(); }}
          className="inline-flex items-center gap-2 border border-emerald-500 bg-emerald-500 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-950 hover:bg-emerald-400">
          <ShareNetwork weight="bold" className="h-4 w-4" /> Share red flag
        </button>
      </div>

      {open && (
        <div className="mt-5 grid gap-5 md:grid-cols-[260px,1fr]" data-testid="share-card-panel">
          <div className="border border-zinc-800 bg-zinc-950 aspect-[4/5] overflow-hidden">
            {card ? <img src={card.dataUrl} alt="Red flag share card" className="h-full w-full object-cover" data-testid="share-card-preview" />
                  : <div className="flex h-full items-center justify-center text-[10px] uppercase tracking-[0.2em] text-zinc-600">{busy ? 'drawing…' : ''}</div>}
          </div>
          <div className="min-w-0">
            <div className="grid grid-cols-3 gap-2">
              <button data-testid="share-native" onClick={nativeShare} className="col-span-3 inline-flex items-center justify-center gap-2 border border-zinc-100 bg-zinc-100 px-3 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-950 hover:bg-white">
                <ShareNetwork weight="bold" className="h-4 w-4" /> Share with image · any app
              </button>
              {platforms(text, url).map(p => (
                <button key={p.id} data-testid={`share-${p.id}`} onClick={() => openPlatform(p)}
                  className="inline-flex flex-col items-center justify-center gap-1.5 border border-zinc-800 px-2 py-3 text-[9px] uppercase tracking-[0.14em] text-zinc-300 hover:border-emerald-500 hover:text-emerald-400">
                  <p.icon weight="fill" className="h-5 w-5" /> {p.label}
                </button>
              ))}
              <button data-testid="share-download" onClick={download} className="inline-flex flex-col items-center justify-center gap-1.5 border border-zinc-800 px-2 py-3 text-[9px] uppercase tracking-[0.14em] text-zinc-300 hover:border-emerald-500 hover:text-emerald-400">
                <DownloadSimple weight="bold" className="h-5 w-5" /> Save image
              </button>
              <button data-testid="share-copy" onClick={copy} className="col-span-2 inline-flex items-center justify-center gap-2 border border-zinc-800 px-2 py-3 text-[9px] uppercase tracking-[0.14em] text-zinc-300 hover:border-emerald-500 hover:text-emerald-400">
                <Copy weight="bold" className="h-4 w-4" /> Copy warning + link
              </button>
            </div>
            <p className="mt-3 text-[10px] text-zinc-600">Platform buttons save the card to your phone and open the app with the warning text — attach the image there. “Share with image” uses your phone’s share sheet and sends the picture directly.</p>
          </div>
        </div>
      )}
    </div>
  );
}
